import { useEffect, useState } from 'react';

import { Avviso, Pulsante, Scheda } from './index';
import { ora } from '../lib/formato';
import { useInstallazione } from '../lib/installazione';
import { LOGO } from '../lib/marchio';

/**
 * Stati trasversali dell'area tiratore:
 *  9a — invito a installare la PWA;
 *  9b — rete assente.
 *
 * Nessuna modalità offline: la shell è in cache, le chiamate API no. Senza rete
 * l'applicazione non può mostrare dati né generare il QR, e lo dice.
 */

// ═══════════════════════════════════════════════════════════ 9a — installa sulla home

const CHIAVE_INVITO = 'poligono.pwa.rifiutato';
const CHIAVE_ACCESSI = 'poligono.accessi';

const PASSI = [
  'Tocca «Aggiungi».',
  'L’icona compare fra le altre app del telefono.',
  'Il QR d’ingresso lo apri in due tocchi.',
];

export function InvitoInstallazione() {
  const { stato, installa } = useInstallazione();
  const [visibile, setVisibile] = useState(false);

  /*
   * L'evento non lo ascolta più questo componente: lo intercetta `lib/installazione`, al
   * caricamento del modulo. Serviva, perché qui l'ascoltatore veniva montato solo se
   * l'invito non era già stato rifiutato — e chi aveva toccato «Non adesso» una volta si
   * ritrovava senza alcun modo di installare l'applicazione, in nessun punto. Ora la porta
   * permanente sta nel profilo; questo resta l'invito spontaneo, con la sua regola.
   */
  useEffect(() => {
    let accessi = 0;
    try {
      if (window.localStorage.getItem(CHIAVE_INVITO) === '1') return;
      accessi = Number(window.localStorage.getItem(CHIAVE_ACCESSI) ?? '0') + 1;
      window.localStorage.setItem(CHIAVE_ACCESSI, String(accessi));
    } catch {
      return;
    }
    if (accessi >= 2) setVisibile(true);
  }, []);

  function rifiuta() {
    try {
      window.localStorage.setItem(CHIAVE_INVITO, '1');
    } catch {
      // niente da fare: l'invito ricomparirà
    }
    setVisibile(false);
  }

  // Il foglio propone il pannello di sistema: senza quello non c'è niente da proporre.
  if (!visibile || stato !== 'pronta') return null;

  // Foglio dal basso sopra un velo, come in 9a: l'invito interrompe, ma senza portare
  // altrove — dietro resta la schermata su cui il tiratore stava lavorando.
  return (
    <div className="velo foglio__velo" role="dialog" aria-modal="true" aria-labelledby="invito">
      <div className="foglio">
        <div className="foglio__maniglia" aria-hidden="true" />

        <div className="foglio__testa">
          <div className="foglio__icona">
            <img src={LOGO} alt="" />
          </div>
          <div>
            <div className="foglio__titolo" id="invito">
              Tienila a portata di mano
            </div>
            <div className="foglio__sottotitolo">
              Aggiungi l’app alla schermata home: si apre a tutto schermo, senza barra del
              browser.
            </div>
          </div>
        </div>

        <ol className="foglio__passi">
          {PASSI.map((passo, i) => (
            <li key={passo}>
              <span className="foglio__numero" aria-hidden="true">
                {i + 1}
              </span>
              <span>{passo}</span>
            </li>
          ))}
        </ol>

        <p className="foglio__nota">
          Serve comunque la connessione: l’app non funziona senza rete.
        </p>

        <Pulsante
          largo
          grande
          onClick={() => {
            void installa();
            setVisibile(false);
          }}
        >
          Aggiungi alla schermata home
        </Pulsante>
        <Pulsante variante="neutro" largo medio onClick={rifiuta}>
          Non adesso
        </Pulsante>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════ 9b — serve la rete

export function SenzaRete({ onRiprova }: { onRiprova: () => void }) {
  const [ultimoTentativo, setUltimoTentativo] = useState(() => new Date().toISOString());

  return (
    <div className="tiratore" data-densita="tattile">
      <main className="tiratore__corpo">
        <Avviso tono="errore" titolo="Non riesco a collegarmi">
          Senza connessione l’app non può mostrare i tuoi dati né generare il QR: tutto
          passa dal server del club.
        </Avviso>

        <Scheda titolo="Prova così" marcata>
          <ol
            style={{
              margin: 0,
              paddingLeft: 20,
              fontSize: 15,
              lineHeight: 1.7,
              color: 'var(--testo-secondario)',
            }}
          >
            <li>Spostati verso la palazzina: dietro le pedane il campo non prende.</li>
            <li>Collegati al wi-fi del club, se lo vedi.</li>
            <li>
              Se sei all’ingresso, dillo al Direttore di Tiro: può accettarti dal tablet.
            </li>
          </ol>
        </Scheda>

        <p className="testo-nota">
          Un QR generato prima di perdere la rete resta valido finché non scade, ma non si
          rigenera da solo: se il countdown è finito, serve la connessione.
        </p>

        <Pulsante
          largo
          grande
          onClick={() => {
            setUltimoTentativo(new Date().toISOString());
            onRiprova();
          }}
        >
          Riprova
        </Pulsante>
        <p className="testo-nota" style={{ textAlign: 'center' }}>
          ultimo tentativo alle {ora(ultimoTentativo)}
        </p>
      </main>
    </div>
  );
}
